import {
  Box,
  Button,
  Flex,
  Icon,
  Tag,
  Text,
  useBreakpointValue,
} from '@chakra-ui/react';
import { CompetitivenessLevel } from '@client/graphql/__generated__/types';
import { MessageSquarePlusIcon } from 'lucide-react';
import { formatPercent } from '~/common/utils/formatter';
import { AdjustCompetitivenessModalTrigger } from './Competitiveness/AdjustCompetitivenessModalTrigger';
import { CompetitivenessAdjustmentPopover } from './Competitiveness/CompetitivenessAdjustmentPopover';
import { CompetitivenessLabel } from './Competitiveness/CompetitivenessLabel';
import { useBuyerOfferAnalysisContext } from './Context/BuyerOfferAnalysisContext';

export const BuyerOfferAnalysisTopTags = () => {
  const {
    buyerOfferAnalysis,
    setIsEditingAgentNotes,
    isEditingAgentNotes,
    agentNotes,
    isViewOnly,
    updateCompetitiveness,
  } = useBuyerOfferAnalysisContext();
  const isMobile = useBreakpointValue({ base: true, lg: false });

  if (!buyerOfferAnalysis) {
    return null;
  }

  const offerRecs = buyerOfferAnalysis.indigoOfferRecs;
  const agentOfferRecs = buyerOfferAnalysis.agentOfferRecs;
  const buyerAgentUser = buyerOfferAnalysis.buyersAgent?.user;

  const agentCompetitivenessAdjustment =
    agentOfferRecs?.competitivenessAdjustment;
  const competitivenessLevel =
    agentCompetitivenessAdjustment?.adjustedLevel ??
    offerRecs?.competitiveness.rating;
  if (
    !competitivenessLevel ||
    competitivenessLevel === CompetitivenessLevel.UNKNOWN ||
    !offerRecs
  ) {
    return null;
  }

  const percentCloseVsAsk = offerRecs.competitiveness.metrics.bidAsk - 1;
  const daysOnMarket = offerRecs.competitiveness.metrics.daysOnMarket;
  const hasAdjustedCompetitiveness =
    agentCompetitivenessAdjustment?.adjustedLevel !==
    offerRecs.competitiveness.rating;

  return (
    <Flex
      alignItems={{ base: 'flex-start', xl: 'center' }}
      direction={{ base: 'column', lg: 'row' }}
      gap={{ base: 1, xl: 0 }}
      justifyContent={{ base: 'flex-start', xl: 'space-between' }}
      px={{ base: 4, xl: 0 }}
      width="100%"
    >
      <Flex
        alignItems="center"
        gap={2}
        justifyContent="flex-start"
        minWidth="0"
        pb={{ base: 2, xl: 0 }}
        {...(isMobile && { overflowX: 'auto' })}
        {...(hasAdjustedCompetitiveness &&
          isMobile && {
            pt: '12px',
          })}
      >
        <Box display="inline-block" flexShrink={0} position="relative">
          <Tag
            _hover={{
              '& .edit-icon-container': {
                opacity: { base: 1, md: 1 },
              },
              cursor: 'default',
            }}
            bg="whiteAlpha.100"
            data-testid="competitiveness-tag"
            flexShrink={0}
            gap={1}
            position="relative"
          >
            {/* !NOTE: on mobile, we will make the entire tag clickable to bring out the edit modal */}
            {(!isMobile || isViewOnly) && (
              <CompetitivenessLabel level={competitivenessLevel} />
            )}

            {!isViewOnly && (
              <AdjustCompetitivenessModalTrigger
                competitiveness={{
                  adjustedLevel: competitivenessLevel,
                  reason: agentCompetitivenessAdjustment?.reason ?? '',
                }}
                onSubmit={updateCompetitiveness}
              />
            )}
          </Tag>
        </Box>
        {agentCompetitivenessAdjustment &&
          hasAdjustedCompetitiveness &&
          buyerAgentUser && (
            <CompetitivenessAdjustmentPopover
              newCompetitiveness={agentCompetitivenessAdjustment.adjustedLevel}
              prevCompetitiveness={offerRecs.competitiveness.rating}
              reason={agentCompetitivenessAdjustment.reason}
              user={buyerAgentUser}
            />
          )}
        <Tag bg="whiteAlpha.100" flexShrink={0} gap={1}>
          <Text fontWeight="medium">
            {`${percentCloseVsAsk >= 0 ? '+' : ''}${formatPercent(
              percentCloseVsAsk
            )}`}{' '}
            market avg sale / list
          </Text>
        </Tag>
        <Tag bg="whiteAlpha.100" flexShrink={0} gap={1}>
          <Text fontWeight="medium">
            {daysOnMarket.toFixed(0)} market avg days on market
          </Text>
        </Tag>
      </Flex>
      {!isEditingAgentNotes && !agentNotes && !isViewOnly && (
        <Button
          borderRadius="48px"
          leftIcon={<Icon as={MessageSquarePlusIcon} color="cyan.500" />}
          px={4}
          size="xs"
          variant="outline"
          width={{ base: '100%', md: 'auto' }}
          onClick={() => setIsEditingAgentNotes(true)}
        >
          <Text color="white" fontWeight="medium">
            Add note to buyer
          </Text>
        </Button>
      )}
    </Flex>
  );
};
